import React, { useEffect, useState } from 'react';
import * as Icon from "@phosphor-icons/react/dist/ssr";

interface ToastProps {
  message: string;
  type: 'success' | 'error' | 'warning' | 'info';
  isVisible: boolean;
  onClose: () => void;
  duration?: number;
}

const Toast: React.FC<ToastProps> = ({ 
  message, 
  type, 
  isVisible, 
  onClose, 
  duration = 4000 
}) => {
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setIsAnimating(true);
      const timer = setTimeout(() => {
        setIsAnimating(false);
        setTimeout(onClose, 300); // Wait for exit animation
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [isVisible, duration, onClose]);

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <Icon.CheckCircle size={20} weight="fill" />;
      case 'error':
        return <Icon.XCircle size={20} weight="fill" />;
      case 'warning':
        return <Icon.Warning size={20} weight="fill" />;
      case 'info':
        return <Icon.Info size={20} weight="fill" />;
      default:
        return <Icon.Info size={20} weight="fill" />;
    }
  };

  const getStyles = () => {
    const baseStyles = "fixed top-6 right-6 z-50 max-w-sm w-full transform transition-all duration-300 ease-out";
    const typeStyles = {
      success: "bg-green-50 border-green-200 text-green-800 shadow-lg shadow-green-100",
      error: "bg-red-50 border-red-200 text-red-800 shadow-lg shadow-red-100",
      warning: "bg-yellow-50 border-yellow-200 text-yellow-800 shadow-lg shadow-yellow-100",
      info: "bg-blue-50 border-blue-200 text-blue-800 shadow-lg shadow-blue-100"
    };
    
    const animationStyles = isAnimating 
      ? "translate-x-0 opacity-100 scale-100" 
      : "translate-x-full opacity-0 scale-95";
    
    return `${baseStyles} ${typeStyles[type]} ${animationStyles}`;
  };

  if (!isVisible) return null;

  return (
    <div className={getStyles()}>
      <div className="flex items-start p-4 rounded-xl border backdrop-blur-sm">
        <div className="flex-shrink-0 mr-3 mt-0.5">
          {getIcon()}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium leading-5">
            {message}
          </p>
        </div>
        <div className="flex-shrink-0 ml-3">
          <button
            onClick={() => {
              setIsAnimating(false);
              setTimeout(onClose, 300);
            }}
            className="inline-flex text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600 transition-colors duration-200"
          >
            <Icon.X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default Toast; 